import { CircleNotch } from "@phosphor-icons/react";
import { type JSX, useState } from "react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import { LeadPreview } from "common/components/leads";
import SearchInput from "common/components/SearchInput/SearchInput";
import { Button } from "common/components/ui/Button";
import { Dialog, OverlayDialogContent } from "common/components/ui/Dialog";
import useDebounce from "common/hooks/useDebounce";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import {
  ContentContainer,
  ContentHeader,
  PageTitle,
} from "common/layouts/WorkspaceLayout";
import type { FilterOptions } from "common/types";

import { ChatContextProvider } from "../context/ChatContext";
import useConversations from "../datahooks/useConversations";
import useActiveConversation from "../hooks/useActiveConversation";
import ConversationContainer from "./ConversationContainer/ConversationContainer";
import ConversationFilters from "./ConversationFilters";
import ConversationLoader from "./ConversationsList/ConversationLoader";
import ConversationsList from "./ConversationsList/ConversationsList";
import FilterConversations from "./FilterConversations";
import InboxEmptyPlaceholder from "./InboxEmptyPlaceholder";
import NoResultsPlaceholder from "./NoResultsPlaceholder";

import SendMessageImage from "assets/images/empty-placeholders/send-a-message.png";

// Main component that provides the chat context to the inbox
export default function Inbox() {
  const isDesktop = useTwBreakpoint("lg");

  const [isLeadPreviewOpen, setIsLeadPreviewOpen] = useState(false);
  const [leadPreviewId, setLeadPreviewId] = useState<string>();

  const [filteredBy, setFilteredBy] = useQueryParam(
    "filter",
    withDefault(StringParam, "all"),
  ) as [FilterOptions, (value: FilterOptions) => void];

  const [selectedFilters, setSelectedFilters] = useState<{
    accountIds: string[];
    campaignIds: string[];
    labelIds: string[];
  }>({
    accountIds: [],
    campaignIds: [],
    labelIds: [],
  });
  const [keywordsInputValue, setKeywordsInputValue] = useState("");
  const [debouncedKeywords, setDebouncedKeywords] = useDebounce(
    keywordsInputValue,
    300,
  );

  const {
    conversations,
    isLoadingConversations,
    loadNextConversations,
    hasMoreConversations,
    refetchConversations,
    conversationsError,
    onUnreadMessage,
  } = useConversations({
    keywords: debouncedKeywords,
    selectedAccountIds: selectedFilters.accountIds,
    selectedCampaignIds: selectedFilters.campaignIds,
    selectedLabelIds: selectedFilters.labelIds,
    filteredBy,
  });

  const { activeConversation, setActiveConversation } = useActiveConversation(
    conversations,
    onUnreadMessage,
  );

  function selectConversation(urn: string, accountId: string) {
    setActiveConversation(urn, accountId);
    if (isLeadPreviewOpen) {
      setIsLeadPreviewOpen(false);
    }
  }

  // Show error placeholder if there is an error fetching conversations
  if (conversationsError && !conversations) {
    return (
      <ContentContainer>
        <ContentHeader>
          <PageTitle />
        </ContentHeader>
        <ErrorPlaceholder
          onRetry={refetchConversations}
          errorMessage="Could not display Conversations"
        />
      </ContentContainer>
    );
  }

  // Check for empty state for the conversations list
  const isConversationsEmpty =
    !isLoadingConversations &&
    (!conversations || !conversations.length) &&
    !debouncedKeywords;

  const hasSelectedFilters =
    !!selectedFilters.accountIds.length ||
    !!selectedFilters.labelIds.length ||
    !!selectedFilters.campaignIds.length;

  // Check for showing empty state on the UI
  const shouldShowEmptyState =
    isConversationsEmpty &&
    !hasSelectedFilters &&
    !debouncedKeywords &&
    filteredBy === "all";

  // Show empty state if there are no conversations and no filters are applied
  if (shouldShowEmptyState) {
    return <InboxEmptyPlaceholder />;
  }

  function showLeadPreview(leadId: string) {
    setIsLeadPreviewOpen(true);
    setLeadPreviewId(leadId);
  }

  function clearKeywords() {
    setDebouncedKeywords("");
    setKeywordsInputValue("");
  }

  function resetActiveConversation() {
    setActiveConversation(null, null);
  }

  const conversationFiltersComponent = (
    <FilterConversations
      activeFilters={selectedFilters}
      onSetActiveFilters={(filters) => {
        setSelectedFilters(filters);
        setIsLeadPreviewOpen(false);
      }}
    />
  );
  const searchInputComponent = (
    <SearchInput
      placeholder="Search conversations"
      className="w-full"
      value={keywordsInputValue}
      onChange={(e) => setKeywordsInputValue(e.target.value)}
      onClear={clearKeywords}
      rightComponent={
        debouncedKeywords &&
        isLoadingConversations && (
          <CircleNotch className="animate-spin text-black-500" />
        )
      }
    />
  );
  const readUnreadFilterComponent = (
    <ConversationFilters
      filteredBy={filteredBy}
      setFilteredBy={(filter) => {
        resetActiveConversation();
        setIsLeadPreviewOpen(false);
        setFilteredBy(filter);
      }}
    />
  );

  let conversationComponent =
    isDesktop && !activeConversation ? (
      <EmptyPlaceholder
        imageSrc={SendMessageImage}
        title="Select a conversation"
        subtitle="Chat with all your leads in one place"
      />
    ) : (
      <ConversationContainer
        activeConversation={activeConversation}
        isLoadingConversations={isLoadingConversations}
        resetActiveConversation={resetActiveConversation}
        showLeadPreview={showLeadPreview}
      />
    );

  let conversationsListComponent: JSX.Element;

  if (isLoadingConversations || conversations?.length) {
    const isInitialLoading = isLoadingConversations && !conversations;

    let endComponent: JSX.Element;
    if (isLoadingConversations) {
      endComponent = <ConversationLoader itemsCount={3} />;
    } else if (conversationsError) {
      endComponent = (
        <div className="flex flex-col p-6 border-t border-t-black-200 items-center">
          <span className="mb-0.5 text-sm-bold">
            Could not load conversations
          </span>
          <p className="mb-4 text-xs-medium text-black-500">
            Something went wrong, please try again
          </p>
          <Button
            size="sm"
            variant="outline-black"
            onClick={() => refetchConversations()}
          >
            Load conversations
          </Button>
        </div>
      );
    }

    conversationsListComponent = isInitialLoading ? (
      <ConversationLoader itemsCount={7} />
    ) : (
      <ConversationsList
        conversations={conversations}
        canFetchNextPage={
          hasMoreConversations && !isLoadingConversations && !conversationsError
        }
        loadMoreConversations={loadNextConversations}
        endComponent={endComponent}
      />
    );
  } else {
    const emptyPlaceholderComponent = (
      <NoResultsPlaceholder
        hasKeywords={!!debouncedKeywords}
        hasOtherFilters={hasSelectedFilters}
        clearKeywords={clearKeywords}
        clearOtherFilters={() => {
          setSelectedFilters({ accountIds: [], labelIds: [], campaignIds: [] });
        }}
      />
    );
    if (isDesktop) {
      conversationComponent = (
        <div className="flex flex-1">{emptyPlaceholderComponent}</div>
      );
    } else {
      conversationsListComponent = emptyPlaceholderComponent;
    }
  }
  return (
    <ChatContextProvider
      value={{ activeConversation, setActiveConversation: selectConversation }}
    >
      <div className="flex gap-x-2 h-full flex-grow">
        <ContentContainer className="lg:max-w-[352px] lg:w-full">
          <ContentHeader>
            <PageTitle />
          </ContentHeader>
          <div className="flex flex-col gap-2.5 p-4">
            <div className="flex gap-2.5">
              {searchInputComponent}
              {conversationFiltersComponent}
            </div>
            {readUnreadFilterComponent}
          </div>

          {conversationsListComponent}
        </ContentContainer>
        {isDesktop ? (
          <>
            <ContentContainer>{conversationComponent}</ContentContainer>
            <LeadPreview
              leadId={leadPreviewId}
              isOpen={isLeadPreviewOpen}
              onClose={() => setIsLeadPreviewOpen(false)}
              isWithSendMessage={false}
            />
          </>
        ) : (
          <Dialog
            open={!!activeConversation}
            onOpenChange={(open) => {
              if (!open) {
                resetActiveConversation();
              }
            }}
          >
            <OverlayDialogContent>
              {activeConversation && conversationComponent}
            </OverlayDialogContent>
          </Dialog>
        )}
      </div>
    </ChatContextProvider>
  );
}
